import {
  addSecuritiesToWatchlist,
  createWatchlist as createWatchlistApi,
  deleteWatchlist as deleteWatchlistApi,
  fetchWatchlistCusipsIndex,
  fetchWatchlists,
  fetchWatchlistSecurities,
  removeSecuritiesFromWatchlist,
  renameWatchlist as renameWatchlistApi,
  type WatchlistSecuritiesFilterRequest,
  type WatchlistSecuritiesResponse,
  type WatchlistSecurityItem,
} from "@/apis/watchlistsApi";
import type { ServiceMutationResult } from "@/shared/types/serviceResult";
import { toServiceErrorMessage } from "@/shared/utils/serviceErrorMessage";
import { OPTIMISTIC_WATCHLIST_ID_PREFIX, type Watchlist } from "@/shared/types/watchlist";
import {
  createPendingTransaction,
  getRollbackSnapshot,
  isPendingTransaction,
  markTransactionCommitted,
  markTransactionFailed,
  markTransactionRolledBack,
} from "@/stores/shared/mutationTransaction";
import type { DomainStore, MutationTransaction, StoreStateMeta } from "@/stores/shared/storeContracts";

export type { WatchlistSecuritiesFilterRequest, WatchlistSecuritiesResponse, WatchlistSecurityItem } from "@/apis/watchlistsApi";

/**
 * Internal store shape only. The base `Watchlist` type mirrors the list-endpoint
 * response, which no longer carries full membership. `cusips` is patched in
 * separately (via `setWatchlistSecurityIdentifiers`) from the detail endpoint,
 * purely to power the cusip -> watchlist reverse index used for the "which
 * watchlists contain this bond" pills feature. It must never be assumed
 * present for a freshly-listed watchlist.
 */
export interface WatchlistWithIdentifiers extends Watchlist {
  cusips?: string[];
}

type WatchlistsMutationPayload =
  | { kind: "create"; watchlist: Watchlist }
  | { kind: "rename"; id: string; name: string }
  | { kind: "delete"; id: string };

interface WatchlistsStoreState {
  watchlistsById: Record<string, WatchlistWithIdentifiers>;
  watchlistIds: string[];
  cusipToWatchlistIds: Record<string, string[]>;
  meta: StoreStateMeta;
  pendingTransactions: Record<string, MutationTransaction<Watchlist[], WatchlistsMutationPayload>>;
}

/**
 * Write-through cache for watchlist data: this is the only place that talks to
 * @/apis/watchlistsApi for watchlists. Callers (services, hooks) go through the
 * methods below instead of calling the API directly, so store state can never
 * be worked around or fall out of sync with what a caller thinks happened.
 */
export interface WatchlistsStore extends DomainStore<WatchlistsStoreState> {
  subscribe(listener: () => void): () => void;
  getVersion(): number;
  getWatchlists(): Watchlist[];
  getWatchlistById(id: string): Watchlist | undefined;
  getWatchlistsForSecurityIdentifier(securityIdentifier: string): Watchlist[];
  setWatchlists(watchlists: Watchlist[]): void;
  upsertWatchlist(watchlist: Watchlist): void;
  replaceWatchlist(previousId: string, watchlist: Watchlist): void;
  removeWatchlist(id: string): void;
  setWatchlistSecurityIdentifiers(watchlistId: string, securityIdentifiers: string[]): void;
  setLoading(isLoading: boolean): void;
  setError(message?: string): void;
  beginOptimisticMutation(payload: WatchlistsMutationPayload): string;
  commitMutation(transactionId: string): void;
  rollbackMutation(transactionId: string): void;
  failMutation(transactionId: string, message: string): void;
  ensureLoaded(): Promise<ServiceMutationResult<Watchlist[]>>;
  loadWatchlists(): Promise<ServiceMutationResult<Watchlist[]>>;
  loadWatchlistSecurities(
    watchlistId: string,
    filters?: WatchlistSecuritiesFilterRequest,
  ): Promise<ServiceMutationResult<WatchlistSecuritiesResponse>>;
  createWatchlist(name: string): Promise<ServiceMutationResult<Watchlist>>;
  renameWatchlist(id: string, name: string): Promise<ServiceMutationResult<Watchlist>>;
  deleteWatchlist(id: string): Promise<ServiceMutationResult<void>>;
  addSecurities(watchlistId: string, securityIdentifiers: string[]): Promise<ServiceMutationResult<WatchlistSecurityItem[]>>;
  removeSecurities(watchlistId: string, securityIdentifiers: string[]): Promise<ServiceMutationResult<WatchlistSecurityItem[]>>;
}

const initialState: WatchlistsStoreState = {
  watchlistsById: {},
  watchlistIds: [],
  cusipToWatchlistIds: {},
  meta: { isLoading: false },
  pendingTransactions: {},
};

let currentState: WatchlistsStoreState = { ...initialState };
let version = 0;
const listeners = new Set<() => void>();

// Guards the one-time list load that the Add-to-Watchlist dialog and both
// watchlist pages depend on. `hasLoadedList` flips only on a successful load so
// a failed first attempt is retried by the next consumer; `listLoadPromise`
// dedupes concurrent callers (bootstrap + a page mounting at the same time)
// onto a single request. Both are cleared on `reset()` so re-auth reloads.
let hasLoadedList = false;
let listLoadPromise: Promise<ServiceMutationResult<Watchlist[]>> | null = null;

// Monotonic token stamped onto each load. `reset()` (and the start of a newer
// load) bumps it so a slower, already-in-flight load that resolves afterwards
// is recognised as stale and makes no state changes — it must not re-set
// `hasLoadedList`, repopulate the emptied store/index, clear a newer load's
// `listLoadPromise`, or overwrite loading/error flags for a session that moved on.
let loadGeneration = 0;

function createTransactionId(): string {
  return `wl-tx-${crypto.randomUUID()}`;
}

function toErrorMessage(error: unknown): string {
  return toServiceErrorMessage(error, "Unexpected watchlists store error.");
}

function toFailureResult<T>(error: unknown, transactionId?: string): ServiceMutationResult<T> {
  return {
    ok: false,
    error: toErrorMessage(error),
    cause: error,
    transactionId,
  };
}

function buildOptimisticWatchlist(name: string): Watchlist {
  const now = new Date().toISOString();
  return {
    id: `${OPTIMISTIC_WATCHLIST_ID_PREFIX}${crypto.randomUUID()}`,
    name,
    isDefault: false,
    bondCount: 0,
    createdAt: now,
  };
}

function cloneWatchlist(watchlist: WatchlistWithIdentifiers): WatchlistWithIdentifiers {
  return {
    ...watchlist,
    cusips: watchlist.cusips ? [...watchlist.cusips] : undefined,
  };
}

function cloneWatchlists(watchlists: WatchlistWithIdentifiers[]): WatchlistWithIdentifiers[] {
  return watchlists.map((watchlist) => cloneWatchlist(watchlist));
}

function normalizeSecurityIdentifier(securityIdentifier: string): string {
  return securityIdentifier.trim().toUpperCase();
}

function compareWatchlists(left: WatchlistWithIdentifiers, right: WatchlistWithIdentifiers): number {
  const nameComparison = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
  if (nameComparison !== 0) {
    return nameComparison;
  }

  return left.id.localeCompare(right.id);
}

function sortWatchlists(watchlists: WatchlistWithIdentifiers[]): WatchlistWithIdentifiers[] {
  return cloneWatchlists(watchlists).sort(compareWatchlists);
}

function buildCusipIndex(watchlists: WatchlistWithIdentifiers[]): Record<string, string[]> {
  const index = new Map<string, Set<string>>();

  for (const watchlist of watchlists) {
    for (const cusip of watchlist.cusips ?? []) {
      const normalized = normalizeSecurityIdentifier(cusip);
      if (!normalized) {
        continue;
      }

      const ids = index.get(normalized) ?? new Set<string>();
      ids.add(watchlist.id);
      index.set(normalized, ids);
    }
  }

  const result: Record<string, string[]> = {};
  for (const [securityIdentifier, ids] of index.entries()) {
    result[securityIdentifier] = [...ids].sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" }),
    );
  }

  return result;
}

function buildState(watchlists: WatchlistWithIdentifiers[], meta: StoreStateMeta, pendingTransactions?: Record<string, MutationTransaction<Watchlist[], WatchlistsMutationPayload>>): WatchlistsStoreState {
  const sortedWatchlists = sortWatchlists(watchlists);
  const watchlistsById: Record<string, WatchlistWithIdentifiers> = {};

  for (const watchlist of sortedWatchlists) {
    watchlistsById[watchlist.id] = cloneWatchlist(watchlist);
  }

  return {
    watchlistsById,
    watchlistIds: sortedWatchlists.map((watchlist) => watchlist.id),
    cusipToWatchlistIds: buildCusipIndex(sortedWatchlists),
    meta: { ...meta },
    pendingTransactions: { ...(pendingTransactions ?? currentState.pendingTransactions) },
  };
}

function snapshotWatchlists(): WatchlistWithIdentifiers[] {
  return currentState.watchlistIds
    .map((id) => currentState.watchlistsById[id])
    .filter((watchlist): watchlist is WatchlistWithIdentifiers => watchlist != null)
    .map((watchlist) => cloneWatchlist(watchlist));
}

function emit(nextState: WatchlistsStoreState): void {
  currentState = nextState;
  version += 1;

  for (const listener of listeners) {
    listener();
  }
}

function updateWatchlistCollection(mutator: (watchlists: WatchlistWithIdentifiers[]) => WatchlistWithIdentifiers[]): void {
  const nextWatchlists = mutator(snapshotWatchlists());
  emit(buildState(nextWatchlists, {
    ...currentState.meta,
    isLoading: currentState.meta.isLoading,
    lastError: undefined,
    lastUpdatedAt: new Date().toISOString(),
  }));
}

function applyOptimisticMutation(
  watchlists: WatchlistWithIdentifiers[],
  payload: WatchlistsMutationPayload,
): WatchlistWithIdentifiers[] {
  if (payload.kind === "create") {
    return [payload.watchlist, ...watchlists];
  }

  if (payload.kind === "rename") {
    return watchlists.map((watchlist) =>
      watchlist.id === payload.id
        ? {
          ...watchlist,
          name: payload.name,
        }
        : watchlist,
    );
  }

  return watchlists.filter((watchlist) => watchlist.id !== payload.id);
}

async function runTransactionalMutation<T>(
  transactionPayload: WatchlistsMutationPayload,
  action: () => Promise<T>,
  applySuccess: (result: T, transactionId: string) => void,
): Promise<ServiceMutationResult<T>> {
  const transactionId = watchlistsStore.beginOptimisticMutation(transactionPayload);

  try {
    const result = await action();
    watchlistsStore.commitMutation(transactionId);
    applySuccess(result, transactionId);

    return {
      ok: true,
      data: result,
      transactionId,
    };
  } catch (error) {
    watchlistsStore.failMutation(transactionId, toErrorMessage(error));
    return toFailureResult<T>(error, transactionId);
  }
}

// The list endpoint no longer returns full membership (securityIdentifiers was
// removed from WatchlistResponse), so the cusip -> watchlist reverse index that
// powers the "which watchlists contain this bond" pills feature would otherwise
// stay empty until a watchlist's detail view was opened. To keep that feature
// working from the moment the watchlist list loads, fetch every (cusip, watchlistId)
// membership pair across all watchlists in a single call (GET /cusips-index) and
// patch it into the store — one round trip instead of one per watchlist.
// Best-effort: a failure here leaves the cusip index empty until the next
// successful fetch, it doesn't fail the overall list load.
async function hydrateSecurityIdentifiersIndex(watchlists: Watchlist[], isCurrent: () => boolean): Promise<void> {
  try {
    const index = await fetchWatchlistCusipsIndex();
    // A `reset()` and/or a newer load can finish while this `/cusips-index`
    // request is in flight. Writing its membership now would overwrite the
    // fresh session's index and clear a newer load's error, so bail out before
    // touching the store if the generation moved on.
    if (!isCurrent()) {
      return;
    }

    const cusipsByWatchlistId = new Map<string, string[]>();
    for (const item of index.items) {
      const cusips = cusipsByWatchlistId.get(item.watchlistId) ?? [];
      cusips.push(item.cusip);
      cusipsByWatchlistId.set(item.watchlistId, cusips);
    }

    for (const watchlist of watchlists) {
      watchlistsStore.setWatchlistSecurityIdentifiers(
        watchlist.id,
        cusipsByWatchlistId.get(watchlist.id) ?? [],
      );
    }
  } catch {
    // Best-effort hydration only; membership simply stays absent from the cusip
    // index until the next successful fetch.
  }
}

// Optimistically merges/removes identifiers into the cusip index without a
// round trip: the mutation response already tells us exactly which identifiers
// were requested, so there's no need to re-fetch the detail endpoint just to
// learn what we already know. Also sidesteps the Gold ES index's indexing lag,
// which a refetch immediately after a write could otherwise race.
function patchWatchlistCusips(watchlistId: string, mutate: (current: string[]) => string[]): void {
  const current = currentState.watchlistsById[watchlistId]?.cusips ?? [];
  watchlistsStore.setWatchlistSecurityIdentifiers(watchlistId, mutate(current));
}

export const watchlistsStore: WatchlistsStore = {
  getState(): WatchlistsStoreState {
    return {
      watchlistsById: Object.fromEntries(
        Object.entries(currentState.watchlistsById).map(([id, watchlist]) => [id, cloneWatchlist(watchlist)]),
      ),
      watchlistIds: [...currentState.watchlistIds],
      cusipToWatchlistIds: Object.fromEntries(
        Object.entries(currentState.cusipToWatchlistIds).map(([cusip, ids]) => [cusip, [...ids]]),
      ),
      meta: { ...currentState.meta },
      pendingTransactions: { ...currentState.pendingTransactions },
    };
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getVersion(): number {
    return version;
  },

  getWatchlists(): Watchlist[] {
    return snapshotWatchlists();
  },

  getWatchlistById(id: string): Watchlist | undefined {
    const watchlist = currentState.watchlistsById[id];
    return watchlist ? cloneWatchlist(watchlist) : undefined;
  },

  getWatchlistsForSecurityIdentifier(securityIdentifier: string): Watchlist[] {
    const normalized = normalizeSecurityIdentifier(securityIdentifier);
    if (!normalized) {
      return [];
    }

    const ids = currentState.cusipToWatchlistIds[normalized] ?? [];
    return ids
      .map((id) => currentState.watchlistsById[id])
      .filter((watchlist): watchlist is Watchlist => watchlist != null)
      .map((watchlist) => cloneWatchlist(watchlist));
  },

  reset(): void {
    hasLoadedList = false;
    listLoadPromise = null;
    loadGeneration += 1;
    emit({
      ...initialState,
      meta: { isLoading: false },
      pendingTransactions: {},
    });
  },

  setWatchlists(watchlists: Watchlist[]): void {
    emit(buildState(watchlists, {
      ...currentState.meta,
      isLoading: false,
      lastError: undefined,
      lastUpdatedAt: new Date().toISOString(),
    }));
  },

  upsertWatchlist(watchlist: Watchlist): void {
    updateWatchlistCollection((watchlists) => {
      const existing = watchlists.find((item) => item.id === watchlist.id);
      const withoutExisting = watchlists.filter((item) => item.id !== watchlist.id);
      // List and add responses omit membership. Keep the hydrated cusips so a
      // following patch appends new identifiers instead of rebuilding from empty.
      const supplied = watchlist as WatchlistWithIdentifiers;
      const cusips = supplied.cusips ?? existing?.cusips;
      return [...withoutExisting, cloneWatchlist({ ...supplied, cusips })];
    });
  },

  replaceWatchlist(previousId: string, watchlist: Watchlist): void {
    updateWatchlistCollection((watchlists) => {
      const withoutPrevious = watchlists.filter((item) => item.id !== previousId);
      return [...withoutPrevious, cloneWatchlist(watchlist)];
    });
  },

  removeWatchlist(id: string): void {
    updateWatchlistCollection((watchlists) => watchlists.filter((watchlist) => watchlist.id !== id));
  },

  setWatchlistSecurityIdentifiers(watchlistId: string, securityIdentifiers: string[]): void {
    updateWatchlistCollection((watchlists) =>
      watchlists.map((watchlist) =>
        watchlist.id === watchlistId
          ? { ...watchlist, cusips: [...securityIdentifiers] }
          : watchlist,
      ),
    );
  },

  setLoading(isLoading: boolean): void {
    emit({
      ...currentState,
      meta: {
        ...currentState.meta,
        isLoading,
        lastUpdatedAt: new Date().toISOString(),
      },
    });
  },

  setError(message?: string): void {
    emit({
      ...currentState,
      meta: {
        ...currentState.meta,
        isLoading: false,
        lastError: message,
        lastUpdatedAt: new Date().toISOString(),
      },
    });
  },

  beginOptimisticMutation(payload: WatchlistsMutationPayload): string {
    const id = createTransactionId();

    const pendingTransaction = createPendingTransaction<Watchlist[], WatchlistsMutationPayload>({
      id,
      action: `watchlists.${payload.kind}`,
      payload,
      rollbackSnapshot: snapshotWatchlists(),
    });

    const nextWatchlists = applyOptimisticMutation(snapshotWatchlists(), payload);
    emit(buildState(nextWatchlists, {
      ...currentState.meta,
      lastUpdatedAt: new Date().toISOString(),
    }, {
      ...currentState.pendingTransactions,
      [id]: pendingTransaction,
    }));

    return id;
  },

  commitMutation(transactionId: string): void {
    const transaction = currentState.pendingTransactions[transactionId];
    if (!transaction || !isPendingTransaction(transaction)) {
      return;
    }

    markTransactionCommitted(transaction);
    const pendingTransactions = { ...currentState.pendingTransactions };
    delete pendingTransactions[transactionId];

    emit({
      ...currentState,
      pendingTransactions,
      meta: {
        ...currentState.meta,
        lastError: undefined,
        lastUpdatedAt: new Date().toISOString(),
      },
    });
  },

  rollbackMutation(transactionId: string): void {
    const transaction = currentState.pendingTransactions[transactionId];
    if (!transaction || !isPendingTransaction(transaction)) {
      return;
    }

    const rolledBack = markTransactionRolledBack(transaction);
    const pendingTransactions = { ...currentState.pendingTransactions };
    delete pendingTransactions[transactionId];

    emit(buildState(getRollbackSnapshot(rolledBack), {
      ...currentState.meta,
      lastUpdatedAt: new Date().toISOString(),
    }, pendingTransactions));
  },

  failMutation(transactionId: string, message: string): void {
    const transaction = currentState.pendingTransactions[transactionId];
    if (!transaction || !isPendingTransaction(transaction)) {
      return;
    }

    const failed = markTransactionFailed(transaction, { message });
    const pendingTransactions = { ...currentState.pendingTransactions };
    delete pendingTransactions[transactionId];

    emit(buildState(getRollbackSnapshot(failed), {
      ...currentState.meta,
      isLoading: false,
      lastError: message,
      lastUpdatedAt: new Date().toISOString(),
    }, pendingTransactions));
  },

  ensureLoaded(): Promise<ServiceMutationResult<Watchlist[]>> {
    if (hasLoadedList) {
      return Promise.resolve({ ok: true, data: watchlistsStore.getWatchlists() });
    }

    if (listLoadPromise) {
      return listLoadPromise;
    }

    const generation = loadGeneration;
    listLoadPromise = watchlistsStore.loadWatchlists().then((result) => {
      // A `reset()` (or a newer load) that ran while this request was in flight
      // bumped the generation; if so, leave the guards for the current session
      // alone — this stale completion must not re-set `hasLoadedList` or null
      // out a newer load's `listLoadPromise`.
      if (generation !== loadGeneration) {
        return result;
      }

      if (result.ok) {
        hasLoadedList = true;
      }
      listLoadPromise = null;
      return result;
    });

    return listLoadPromise;
  },

  async loadWatchlists(): Promise<ServiceMutationResult<Watchlist[]>> {
    // Stamp this load so a `reset()` arriving mid-flight can veto its writes:
    // otherwise a slow fetch that resolves after reset would repopulate the
    // store, index, and error/loading flags for a session that already moved on.
    const generation = loadGeneration;
    const isCurrent = () => generation === loadGeneration;

    watchlistsStore.setLoading(true);
    watchlistsStore.setError(undefined);

    try {
      const watchlists = await fetchWatchlists();
      if (!isCurrent()) {
        return { ok: true, data: watchlists };
      }

      watchlistsStore.setWatchlists(watchlists);
      await hydrateSecurityIdentifiersIndex(watchlists, isCurrent);

      return {
        ok: true,
        data: watchlists,
      };
    } catch (error) {
      if (isCurrent()) {
        watchlistsStore.setLoading(false);
        watchlistsStore.setError(toErrorMessage(error));
      }
      return toFailureResult<Watchlist[]>(error);
    }
  },

  async loadWatchlistSecurities(
    watchlistId: string,
    filters?: WatchlistSecuritiesFilterRequest,
  ): Promise<ServiceMutationResult<WatchlistSecuritiesResponse>> {
    try {
      const data = await fetchWatchlistSecurities(watchlistId, filters);
      watchlistsStore.setWatchlistSecurityIdentifiers(
        watchlistId,
        data.items.map((item) => item.securityIdentifier),
      );
      return {
        ok: true,
        data,
      };
    } catch (error) {
      return toFailureResult<WatchlistSecuritiesResponse>(error);
    }
  },

  async createWatchlist(name: string): Promise<ServiceMutationResult<Watchlist>> {
    const optimisticWatchlist = buildOptimisticWatchlist(name);
    return runTransactionalMutation(
      { kind: "create", watchlist: optimisticWatchlist },
      () => createWatchlistApi(name),
      (created) => {
        watchlistsStore.replaceWatchlist(optimisticWatchlist.id, created);
      },
    );
  },

  async renameWatchlist(id: string, name: string): Promise<ServiceMutationResult<Watchlist>> {
    return runTransactionalMutation(
      { kind: "rename", id, name },
      () => renameWatchlistApi(id, name),
      (renamed) => {
        watchlistsStore.upsertWatchlist(renamed);
      },
    );
  },

  async deleteWatchlist(id: string): Promise<ServiceMutationResult<void>> {
    return runTransactionalMutation(
      { kind: "delete", id },
      () => deleteWatchlistApi(id),
      () => {
        watchlistsStore.removeWatchlist(id);
      },
    );
  },

  async addSecurities(
    watchlistId: string,
    securityIdentifiers: string[],
  ): Promise<ServiceMutationResult<WatchlistSecurityItem[]>> {
    try {
      const updated = await addSecuritiesToWatchlist(watchlistId, securityIdentifiers);
      watchlistsStore.upsertWatchlist(updated);

      const added = new Set(securityIdentifiers.map((identifier) => normalizeSecurityIdentifier(identifier)));
      patchWatchlistCusips(watchlistId, (current) => [
        ...current,
        ...[...added].filter((identifier) => !current.some((existing) => normalizeSecurityIdentifier(existing) === identifier)),
      ]);

      return { ok: true, data: [] };
    } catch (error) {
      return toFailureResult<WatchlistSecurityItem[]>(error);
    }
  },

  async removeSecurities(
    watchlistId: string,
    securityIdentifiers: string[],
  ): Promise<ServiceMutationResult<WatchlistSecurityItem[]>> {
    try {
      const updated = await removeSecuritiesFromWatchlist(watchlistId, securityIdentifiers);
      watchlistsStore.upsertWatchlist(updated);

      const removed = new Set(securityIdentifiers.map((identifier) => normalizeSecurityIdentifier(identifier)));
      patchWatchlistCusips(watchlistId, (current) =>
        current.filter((existing) => !removed.has(normalizeSecurityIdentifier(existing))),
      );

      return { ok: true, data: [] };
    } catch (error) {
      return toFailureResult<WatchlistSecurityItem[]>(error);
    }
  },
};
